import React, { useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { Modal, ModalBody } from '../../ui';
import Table, {
  TableCell,
  TableHeader,
  TableHeaders,
  TableRow,
} from '../../ui/Table';
import { Text } from '../../ui';
import theme from '../../theme';
import type { SuggestionLogEntry, SuggestionResult } from './getSuggestions';
import type { ScenarioOrder } from '../../../lib/planningData';
import type { ModalWithResultProps } from '../../hooks/useModalWithResult';
import { createReportFromLogs } from './createReportFromLogs';
import { memoize, orderBy, sumBy } from 'lodash';
import { mapGroupBy, mapGroupByMany } from '@seaber/common/lib/collections';
import type { ScheduleMetrics } from '../scheduleMetrics';
import type { RequiredNonNullable } from '@seaber/common/lib/generics';
import { assertedDefined, isDefined, keys } from '@seaber/common/lib/generics';
import { mapTransform, roundTo } from '../../../lib/util';
import type { Vessel } from '../Vessel';
import { parseAmountInfo } from '../../order/order';

interface SummaryItem {
  label: string;
  value: string | number;
}

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  background-color: ${themeGet('colors.neutral.black_500')};
  color: ${themeGet('colors.neutral.white_50')};
  border-bottom: 1px solid ${themeGet('colors.neutral.gray_600')};
  position: sticky;
  top: 0;
  z-index: 10;
  width: 100%;
  box-sizing: border-box;
  max-width: 1200px;
  margin: 0 auto;
`;

const Panel = styled.div<{ maxHeight?: string }>`
  background-color: ${themeGet('colors.neutral.white_50')};
  border-radius: 4px;
  border: 1px solid ${themeGet('colors.neutral.gray_200')};
  margin-bottom: 16px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  ${(props) =>
    props.maxHeight &&
    `
    max-height: ${props.maxHeight};
    overflow-y: auto;
  `}
`;

const PanelHeader = styled.div`
  padding: 12px 16px;
  font-weight: 600;
  font-size: 14px;
  border-bottom: 1px solid ${themeGet('colors.neutral.gray_200')};
  background-color: ${themeGet('colors.neutral.gray_50')};
`;

const PanelBody = styled.div`
  padding: 16px;
`;

const SearchInput = styled.input`
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid ${themeGet('colors.neutral.gray_200')};
  background-color: ${themeGet('colors.neutral.white_50')};
  flex: 1;
  font-size: 14px;
  &:focus {
    outline: none;
    border-color: ${themeGet('colors.yellow.yellow_300')};
  }
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid ${themeGet('colors.neutral.gray_200')};
  background-color: ${themeGet('colors.neutral.white_50')};
  font-size: 14px;
  margin-left: 8px;
  &:focus {
    outline: none;
    border-color: ${themeGet('colors.yellow.yellow_300')};
  }
`;

const FilterContainer = styled.div`
  margin-bottom: 16px;
  display: flex;
  justify-content: flex-end;
`;

const TableWrapper = styled.div<{ maxHeight?: string; height?: string }>`
  ${(props) =>
    props.maxHeight &&
    `
    max-height: ${props.maxHeight};
    overflow-y: auto;
  `}
  ${(props) =>
    props.height &&
    `
    height: ${props.height};
    overflow-y: auto;
  `}
  border: 1px solid ${themeGet('colors.neutral.gray_200')};
  border-radius: 4px;
`;

const StyledTable = styled(Table)<{ maxHeight?: string }>`
  margin-top: 0;
  border: none;
  border-radius: 0;
  overflow: hidden;

  ${TableHeaders} {
    background-color: ${themeGet('colors.neutral.gray_100')};
  }

  ${TableHeader} {
    font-weight: 600;
    font-size: 13px;
    color: ${themeGet('colors.neutral.gray_700')};
    padding: 10px 16px;
  }

  ${TableCell} {
    padding: 10px 16px;
    font-size: 13px;
    border-bottom: 1px solid ${themeGet('colors.neutral.gray_200')};
  }
`;

const OrdersTable = styled(StyledTable)`
  ${TableRow} {
    cursor: pointer;
  }

  ${TableCell} {
    cursor: inherit;
  }

  ${TableRow}:hover {
    background-color: ${themeGet('colors.neutral.gray_50')};
  }

  ${TableRow}.selected {
    background-color: ${themeGet('colors.neutral.gray_100')};
  }
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin: 24px 0 16px 0;
  color: ${themeGet('colors.neutral.black_900')};
  &:first-of-type {
    margin-top: 0;
  }
`;

const ChartContainer = styled.div`
  margin-top: 16px;
  height: 30px;
  display: flex;
  width: 100%;
  border-radius: 4px;
  overflow: hidden;
`;

const ChartSegment = styled.div<{ width: number; color: string }>`
  width: ${(props) => props.width}%;
  height: 100%;
  background-color: ${(props) => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => {
    // Determine if text should be dark or light based on background color brightness
    const hex = props.color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128 ? '#000000' : '#ffffff';
  }};
  font-size: 12px;
  font-weight: 600;
`;

const LegendContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 12px;
`;

const LegendItem = styled.div<{ title: string }>`
  display: flex;
  align-items: center;
  font-size: 13px;
  color: ${themeGet('colors.neutral.gray_700')};
  cursor: default;
  &:hover {
    color: ${themeGet('colors.neutral.black_900')};
  }
`;

const LegendColor = styled.div<{ color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background-color: ${(props) => props.color};
  margin-right: 8px;
  display: inline-block;
`;

const SummaryTable = styled.div`
  display: flex;
  width: 100%;
  margin-top: 16px;
  border-radius: 4px;
  overflow: hidden;
  align-items: flex-start;
`;

const SummaryColumn = styled.div`
  flex: 1;
  border: 1px solid ${themeGet('colors.neutral.gray_200')};
  border-radius: 4px;
  overflow: hidden;
  align-self: flex-start;

  &:first-child {
    margin-right: 12px;
  }
`;

const SummaryRow = styled.div<{ isEven?: boolean }>`
  display: flex;
  background-color: ${(props) =>
    props.isEven
      ? themeGet('colors.neutral.white_50')(props)
      : themeGet('colors.neutral.white_100')(props)};
  border-bottom: 1px solid ${themeGet('colors.neutral.gray_200')};

  &:last-child {
    border-bottom: none;
  }
`;

const SummaryLabel = styled.div`
  padding: 12px 16px;
  font-size: 13px;
  font-weight: 500;
  color: ${themeGet('colors.neutral.gray_700')};
  border-right: 1px solid ${themeGet('colors.neutral.gray_200')};
  flex: 1;
`;

const SummaryValue = styled.div`
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 600;
  color: ${themeGet('colors.neutral.black_900')};
  width: 5em;
`;

const ReportContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;

  @media (min-width: 1024px) {
    display: flex;
    flex-direction: row;
    gap: 20px;
    max-height: calc(90vh - 65px);
    overflow: hidden;
  }
`;

const MainContent = styled.div`
  width: 100%;

  @media (min-width: 1024px) {
    flex: 1.1;
    overflow-y: auto;
  }
`;

const SelectionIndicators = styled.div`
  width: 100%;
  margin-top: 16px;

  @media (min-width: 1024px) {
    flex: 1;
    overflow-y: auto;
    margin-top: 0;
  }
`;

const getBarColor = (colorIndex: number): string => {
  const C = theme.colors;
  // Base colors from theme
  const baseColors = [
    C.yellow.yellow_300,
    C.orange.orange_500,
    C.neutral.gray_500,
    C.orange.orange_600,
    C.neutral.gray_400,
    C.neutral.gray_300,
  ];

  const baseIndex = colorIndex % baseColors.length;
  const baseColor = baseColors[baseIndex];

  // If count is within base colors range, return the base color
  if (colorIndex < baseColors.length) {
    return baseColor;
  }

  // Just in case we'd have very many items in the chart, make room for additional colors
  const variationIndex = Math.floor(colorIndex / baseColors.length);
  const brightnessAdjustment = ((variationIndex % 3) - 1) * 10;

  return adjustColorBrightness(baseColor, brightnessAdjustment);
};

/** Returns a consistent color palette for a list of items. Makes a special case for valid/selected items which get the seaber yellow color, and no rejection item should use it.
 * It is assumed that the valid/selected item will always be first, if present in the array. */
const getColorPalette = memoize(
  (
    items: Array<{
      reason?: SuggestionLogEntry['reason'];
      violation?: unknown;
    }>,
  ): string[] => {
    const includesValid = items.some(
      (i) => i.reason === 'is_valid' || i.reason === 'selected',
    );

    return items.map((_, i) => getBarColor(i + (includesValid ? 0 : 1)));
  },
);

const adjustColorBrightness = (hex: string, percent: number): string => {
  // Convert hex to RGB
  let r = parseInt(hex.substring(1, 3), 16);
  let g = parseInt(hex.substring(3, 5), 16);
  let b = parseInt(hex.substring(5, 7), 16);

  // Adjust brightness
  r = Math.max(0, Math.min(255, r + percent));
  g = Math.max(0, Math.min(255, g + percent));
  b = Math.max(0, Math.min(255, b + percent));

  // Convert back to hex
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

/** Adds the relative percentage value to each item in the array */
function valuesToPercentages<T extends { value: number }>(
  arr: T[],
): (T & { pct: number })[] {
  const total = sumBy(arr, (x) => x.value);
  return arr.map((v) => ({ ...v, pct: (v.value / total) * 100 }));
}

const LOG_REASON_TO_LABEL: Record<SuggestionLogEntry['reason'], string> = {
  order_vessel_requirements: 'Order vessel requirements',
  cargo_exclusion: 'Cargo exlusion',
  laycan_in_past: 'Laycan in past',
  excessive_idle: 'Excessive idle',
  duplicate_port_pair: 'Duplicate port pair',
  no_possible_ports: 'No possible ports',
  insufficient_volume_capacity: 'Insufficient volume capacity',
  distance_from_ports: 'Distance from ports',
  selected: 'Selected',
  metrics_violation: 'Parameter violation',
  not_best_schedule: 'Better schedule exists',
  low_solution_vcp: 'Low solution VCP',
  is_valid: 'Valid',
  identical_voyage: 'Identical voyage',
  no_valid_schedule: 'No valid schedule',
};

const LOG_REASON_TO_HINT: Record<SuggestionLogEntry['reason'], string> = {
  order_vessel_requirements: 'Order and vessel attributes do not match.',
  cargo_exclusion: 'Cargo exclusin.',
  laycan_in_past: 'Laycan in past.',
  excessive_idle:
    'Including this order would result in excessive idle time for the vessel.',
  duplicate_port_pair:
    'We had already attempted another order with the same port pair.',
  no_possible_ports:
    'No valid ports. This can happen if the last voyage on the timeline is already in the past.',
  insufficient_volume_capacity:
    'The vessel did not have enough capacity for the cargo considering other included orders.',
  distance_from_ports:
    'The L/D ports are too far from existing ports in the schedule.',
  selected:
    'How many times the order was selected for further processing. A single selection may yield multiple suggestions when the selection happens early in the process and we try adding other orders to its schedule.',
  metrics_violation:
    'Including the order would violate schedule parameters. The solution might be included if you adjust the parameters.',
  not_best_schedule: 'A better schedule exists for this order combination.',
  low_solution_vcp: 'There were combinations with better potential.',
  is_valid: 'Valid and considered.',
  identical_voyage:
    'The voyage was identical to another already generated voyage.',
  no_valid_schedule:
    'No valid schedule could be created for the order combination.',
};

const METRICS_VIOLATION_TO_LABEL: Record<
  keyof ScheduleMetrics['violations'],
  string
> = {
  newAssignmentHasExclusionViolation: 'Assortment exclusion',
  maxEarlyMs: 'Cargo too early',
  maxLateMs: 'Cargo too late',
  totalDelayH: 'Schedule total delay',
  maxBallastDistanceNm: 'Ballast distance',
};

const METRICS_VIOLATION_TO_HINT: Record<
  keyof ScheduleMetrics['violations'],
  string
> = {
  newAssignmentHasExclusionViolation:
    'Including the order would violate assortment exclusion rules.',
  maxEarlyMs:
    'The vessel would arrive too early according to the schedule parameters.',
  maxLateMs:
    'The vessel would arrive too late according to the schedule parameters.',
  totalDelayH:
    'Including the order would cause excessive total delay in the schedule.',
  maxBallastDistanceNm:
    'The ballast distance would be more than allowed by parameters.',
};

export type VoyageSuggestionReportParams = {
  suggestionResult: RequiredNonNullable<SuggestionResult>;
  orderById: Map<number, ScenarioOrder>;
  vesselById: Map<number, Vessel>;
};

export type VoyageSuggestionReportResult = null;

export type VoyageSuggestionReportProps = ModalWithResultProps<
  VoyageSuggestionReportResult,
  VoyageSuggestionReportParams
>;

type OrderRowData = {
  id: number;
  assortment: string;
  suggestionCount: number;
  from: string;
  to: string;
  amount: string;
};

function OrderFiltering({
  orderRows,
  selectedOrderId,
  setSelectedOrderId,
}: {
  orderRows: OrderRowData[];
  selectedOrderId: number | null;
  setSelectedOrderId: (id: number | null) => void;
}) {
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Filter orders based on search query
  const filteredOrderData = useMemo(() => {
    if (!searchQuery) return orderRows;

    const query = searchQuery.toLowerCase();
    return orderRows.filter(
      (order) =>
        order.id.toString().toLowerCase().includes(query) ||
        order.assortment.toLowerCase().includes(query) ||
        order.suggestionCount.toString().toLowerCase().includes(query) ||
        order.from.toLowerCase().includes(query) ||
        order.to.toLowerCase().includes(query) ||
        order.amount.toLowerCase().includes(query),
    );
  }, [orderRows, searchQuery]);

  return (
    <Panel>
      <PanelHeader>Orders</PanelHeader>
      <PanelBody>
        <FilterContainer>
          <SearchInput
            placeholder="Search by any table column"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
          />
        </FilterContainer>

        <TableWrapper height="220px">
          <OrdersTable>
            <TableHeaders>
              <TableHeader>Order ID</TableHeader>
              <TableHeader>From</TableHeader>
              <TableHeader>To</TableHeader>
              <TableHeader>Amount</TableHeader>
              <TableHeader>Commodity</TableHeader>
              <TableHeader>In suggestions</TableHeader>
            </TableHeaders>
            {filteredOrderData.map((order) => (
              <TableRow
                key={order.id}
                className={order.id === selectedOrderId ? 'selected' : ''}
                onClick={() =>
                  setSelectedOrderId(
                    order.id === selectedOrderId ? null : order.id,
                  )
                }
              >
                <TableCell>{order.id}</TableCell>
                <TableCell>{order.from}</TableCell>
                <TableCell>{order.to}</TableCell>
                <TableCell>{order.amount}</TableCell>
                <TableCell>{order.assortment}</TableCell>
                <TableCell>{order.suggestionCount}</TableCell>
              </TableRow>
            ))}
          </OrdersTable>
        </TableWrapper>
      </PanelBody>
    </Panel>
  );
}

export function VoyageSuggestionReport({
  suggestionResult,
  orderById: allOrdersById,
  vesselById: allVesselById,
  onCancel,
}: VoyageSuggestionReportProps) {
  const { logs, suggestionsByVessel } = suggestionResult;

  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [selectedVesselId, setSelectedVesselId] = useState<number | null>(null);

  const report = createReportFromLogs(
    logs,
    suggestionsByVessel,
    selectedVesselId ? new Set([selectedVesselId]) : undefined,
  );

  const orderById = useMemo(
    () =>
      new Map(
        [...allOrdersById].filter(([_, o]) =>
          report.orders.available.has(o.id),
        ),
      ),
    [report, allOrdersById],
  );

  const orderIncidencesInSuggestions = useMemo(() => {
    const allSuggestions = [...suggestionsByVessel.values()].flat();
    const suggestionsByOrder = mapGroupByMany(allSuggestions, (s) =>
      s.schedule.combination.assignments.map((a) => a.order.id),
    );
    return mapTransform(
      suggestionsByOrder,
      (suggestions) => suggestions.length,
    );
  }, [suggestionsByVessel]);

  /** Data for orders table */
  const orderRows = useMemo((): OrderRowData[] => {
    const orderRows = [...orderById.values()].map((order) => {
      const amountStr =
        parseAmountInfo({
          ...order,
          cargoAmount:
            order.cargoUnitByCargoUnitId?.name === 'pcs' ||
            order.cargoUnitByCargoUnitId?.name === 'set'
              ? order.cargoAmount
              : roundTo(order.cargoAmount, 0),
        }) ?? '';

      return {
        id: order.id,
        assortment: order.assortmentByAssortmentId?.name ?? 'Unknown',
        suggestionCount: orderIncidencesInSuggestions.get(order.id) ?? 0,
        from: order.locationByLoadPortId?.name ?? 'Unknown',
        to: order.locationByDischargePortId?.name ?? 'Unknown',
        amount: amountStr,
      };
    });

    return orderBy(orderRows, (o) => o.suggestionCount, 'desc');
  }, [orderById, orderIncidencesInSuggestions]);

  const availableVessels = useMemo(
    () =>
      Array.from(logs.logsByVessel.keys())
        .map((v) => allVesselById.get(v))
        .filter(isDefined),
    [logs.logsByVessel, allVesselById],
  );

  const shownVessels = useMemo(
    () =>
      availableVessels.filter(
        (v) => !selectedVesselId || selectedVesselId === v.id,
      ),
    [availableVessels, selectedVesselId],
  );

  /** Data for vessels table */
  const vesselStatistics = useMemo(
    () =>
      shownVessels.map((vessel) => {
        const vesselReport = assertedDefined(
          report.vesselReports.get(vessel.id),
          `expected vessel ${vessel.id} in reports`,
        );
        return [
          vessel,
          {
            ordersEvaluated: vesselReport.orders.valid.size,
            ordersInSuggestions: (
              suggestionsByVessel.get(vessel.id) || []
            ).reduce(
              (count, suggestion) =>
                count + suggestion.schedule.combination.assignments.length,
              0,
            ),
            combinationsTested: vesselReport.totalCombinationCount,
          },
        ] as const;
      }),
    [shownVessels, report.vesselReports, suggestionsByVessel],
  );

  /** Data for orders summary table */
  const ordersSummaryData = [
    {
      label: 'Orders total',
      value: report.orders.available.size,
    },
    {
      label: 'Orders evaluated',
      value: report.orders.valid.size,
    },
    {
      label: 'Orders in suggestions',
      value: report.orders.inSuggestions.size,
    },
  ];

  for (const [reason, entries] of [
    ...report.orders.invalidByReason.entries(),
    ...report.topLevelInvalidByReason.entries(),
  ]) {
    if (entries.length > 0) {
      const label = LOG_REASON_TO_LABEL[reason];
      ordersSummaryData.push({
        label,
        value: sumBy(entries, (e) => e.orderIds.length),
      });
    }
  }

  /** Data for schedules summary table */
  const schedulesSummaryData = [
    {
      label: 'Total Combinations',
      value: report.totalCombinationCount,
    },
    {
      label: 'Valid Combinations',
      value: report.validCombinationCount,
    },
    {
      label: 'Tested Combinations',
      value: report.totalCombinationCount,
    },
  ];

  const filterLogsBySelectedOrder = useCallback(
    <T extends SuggestionLogEntry>(entries: T[] | undefined): T[] => {
      if (selectedOrderId === null) {
        return entries ?? [];
      }
      return entries?.filter((e) => e.orderIds.includes(selectedOrderId)) ?? [];
    },
    [selectedOrderId],
  );

  /**
   * Counts the number of order occurrences in entries consistently
   * regardless of whether a specific order is selected or not
   */
  const countOrderEntries = useCallback(
    <T extends SuggestionLogEntry>(entries: T[]): number => {
      if (selectedOrderId === null) {
        // Count every order across all entries
        return sumBy(entries, (e) => e.orderIds.length);
      } else {
        // When filtered by order, just count the entries
        // (each selected entry will have the order exactly once)
        return entries.length;
      }
    },
    [selectedOrderId],
  );

  /** Data for the pre-processing section */
  const preProcessingData = useMemo(() => {
    const preProcessingByReason = mapGroupBy(
      filterLogsBySelectedOrder(report.allLogEntries).filter(
        (e) => e.stage === 'preprocessing',
      ),
      (e) => e.reason,
    );

    return valuesToPercentages(
      [...preProcessingByReason.entries()]
        .sort(([a], [b]) => (a === 'is_valid' ? -1 : b === 'is_valid' ? 1 : 0))
        .map(([label, entries]) => ({
          label: LOG_REASON_TO_LABEL[label],
          reason: label,
          value: countOrderEntries(entries),
        })),
    );
  }, [filterLogsBySelectedOrder, countOrderEntries, report.allLogEntries]);

  /** Data for the order pre-selection section */
  const orderPreSelectionData = useMemo(() => {
    if (!report.shortlistingAllEntries.length) return null;

    const data = [
      {
        label: 'Selected',
        value: countOrderEntries(
          filterLogsBySelectedOrder(report.shortListingAcceptedEntries),
        ),
        reason: 'selected' as SuggestionLogEntry['reason'],
      },
    ];

    for (const [
      reason,
      entries,
    ] of report.shortlistingRejectedByReason.entries()) {
      data.push({
        label: LOG_REASON_TO_LABEL[reason],
        reason,
        value: countOrderEntries(filterLogsBySelectedOrder(entries)),
      });
    }

    return valuesToPercentages(data.filter((d) => d.value > 0));
  }, [
    report.shortlistingAllEntries.length,
    report.shortListingAcceptedEntries,
    report.shortlistingRejectedByReason,
    filterLogsBySelectedOrder,
    countOrderEntries,
  ]);

  /** Data for the scheduling section */
  const schedulingData = useMemo(() => {
    if (!report.schedulingAllEntriesByReason.size) return null;

    const data = [
      {
        label: 'Selected',
        value: filterLogsBySelectedOrder(
          report.schedulingAllEntriesByReason.get('selected'),
        ).length,
        reason: 'selected' as SuggestionLogEntry['reason'],
      },
    ];

    for (const [
      reason,
      entries,
    ] of report.schedulingAllEntriesByReason.entries()) {
      const filteredEntries = filterLogsBySelectedOrder(entries);
      if (reason !== 'selected' && filteredEntries.length > 0) {
        data.push({
          label: LOG_REASON_TO_LABEL[reason],
          value: filteredEntries.length,
          reason,
        });
      }
    }

    if (!data.find((d) => d.value > 0)) return null;

    return valuesToPercentages(data);
  }, [filterLogsBySelectedOrder, report.schedulingAllEntriesByReason]);

  /** Data for the violations section */
  const violationsData = useMemo(() => {
    const violationEntries = filterLogsBySelectedOrder(
      report.allLogEntries.filter((e) => e.reason === 'metrics_violation'),
    );
    const byViolation = mapGroupByMany(violationEntries, (e) =>
      keys(e.violations),
    );
    return valuesToPercentages(
      [...byViolation.entries()].map(([violation, entries]) => ({
        label: METRICS_VIOLATION_TO_LABEL[violation],
        value: entries.length,
        violation,
      })),
    );
  }, [filterLogsBySelectedOrder, report.allLogEntries]);

  return (
    <Modal
      isOpen={true}
      onClose={onCancel}
      width="95vw"
      maxWidth="1200px"
      height="auto"
    >
      <ModalHeader>
        <Text fontSize="16px" fontWeight="bold" color="neutral.white_50">
          Voyage Suggestion Report
        </Text>
      </ModalHeader>
      <ModalBody
        padding="16px"
        backgroundColor="neutral.white_50"
        overflowY="auto"
        overflowX="hidden"
        display="flex"
        flexDirection="column"
      >
        <ReportContainer>
          <MainContent>
            {availableVessels.length > 1 && (
              <FilterContainer>
                <FilterSelect
                  value={selectedVesselId || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedVesselId(value ? parseInt(value, 10) : null);
                  }}
                >
                  <option value="">All Vessels</option>
                  {availableVessels.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                    </option>
                  ))}
                </FilterSelect>
              </FilterContainer>
            )}
            <Panel>
              <PanelHeader>Computation Summary</PanelHeader>
              <PanelBody>
                <SummaryTable>
                  <SummaryColumn>
                    {ordersSummaryData.map(
                      (item: SummaryItem, rowIndex: number) => (
                        <SummaryRow key={rowIndex} isEven={rowIndex % 2 === 1}>
                          <SummaryLabel>{item.label}</SummaryLabel>
                          <SummaryValue>{item.value}</SummaryValue>
                        </SummaryRow>
                      ),
                    )}
                  </SummaryColumn>
                  <SummaryColumn>
                    {schedulesSummaryData.map(
                      (item: SummaryItem, rowIndex: number) => (
                        <SummaryRow key={rowIndex} isEven={rowIndex % 2 === 1}>
                          <SummaryLabel>{item.label}</SummaryLabel>
                          <SummaryValue>{item.value}</SummaryValue>
                        </SummaryRow>
                      ),
                    )}
                  </SummaryColumn>
                </SummaryTable>
              </PanelBody>
            </Panel>

            {vesselStatistics && (
              <Panel>
                <PanelHeader>Vessels</PanelHeader>
                <PanelBody>
                  <TableWrapper maxHeight="220px">
                    <StyledTable>
                      <TableHeaders>
                        <TableHeader>Vessel</TableHeader>
                        <TableHeader>Orders Evaluated</TableHeader>
                        <TableHeader>Orders in Suggestions</TableHeader>
                        <TableHeader>Combinations Tested</TableHeader>
                      </TableHeaders>
                      {vesselStatistics.map(([vessel, stats]) => (
                        <TableRow key={vessel.id}>
                          <TableCell>{vessel.name}</TableCell>
                          <TableCell>{stats.ordersEvaluated}</TableCell>
                          <TableCell>{stats.ordersInSuggestions}</TableCell>
                          <TableCell>{stats.combinationsTested}</TableCell>
                        </TableRow>
                      ))}
                    </StyledTable>
                  </TableWrapper>
                </PanelBody>
              </Panel>
            )}

            <OrderFiltering
              orderRows={orderRows}
              selectedOrderId={selectedOrderId}
              setSelectedOrderId={setSelectedOrderId}
            />
          </MainContent>

          <SelectionIndicators>
            <Panel>
              <PanelHeader>
                Selection indicators
                {selectedOrderId ? ` for order #${selectedOrderId}` : ''}
              </PanelHeader>
              <PanelBody>
                {preProcessingData.length > 0 && (
                  <>
                    <SectionTitle>Order compatibility</SectionTitle>
                    <Text
                      fontSize="13px"
                      color="neutral.gray_600"
                      marginBottom="12px"
                    >
                      <p>
                        Inclusion of orders based on laycan and vessel
                        attributes.
                      </p>
                    </Text>

                    <ChartContainer>
                      {preProcessingData.map((data, i) => (
                        <ChartSegment
                          width={data.pct}
                          color={getColorPalette(preProcessingData)[i]}
                          key={i}
                        >
                          {Math.ceil(data.pct)}%
                        </ChartSegment>
                      ))}
                    </ChartContainer>

                    <LegendContainer>
                      {preProcessingData.map((data, i) => (
                        <LegendItem
                          key={i}
                          title={LOG_REASON_TO_HINT[data.reason]}
                        >
                          <LegendColor
                            color={getColorPalette(preProcessingData)[i]}
                          />
                          {data.label}: {data.value}
                        </LegendItem>
                      ))}
                    </LegendContainer>
                  </>
                )}

                {orderPreSelectionData && orderPreSelectionData.length > 0 && (
                  <>
                    <SectionTitle>Order pre-selection</SectionTitle>
                    <Text
                      fontSize="13px"
                      color="neutral.gray_600"
                      marginBottom="12px"
                    >
                      <p>
                        Criteria by which orders did not pass the initial check
                        for inclusion. The same order can be valid for one
                        combination and discarded for another.
                      </p>{' '}
                      <p>
                        It is normal for only a handful of orders to pass this
                        phase.
                      </p>
                    </Text>

                    <ChartContainer>
                      {orderPreSelectionData.map((data, i) => (
                        <ChartSegment
                          width={data.pct}
                          color={getColorPalette(orderPreSelectionData)[i]}
                          key={i}
                        >
                          {Math.ceil(data.pct)}%
                        </ChartSegment>
                      ))}
                    </ChartContainer>

                    <LegendContainer>
                      {orderPreSelectionData.map((data, i) => (
                        <LegendItem
                          key={i}
                          title={LOG_REASON_TO_HINT[data.reason]}
                        >
                          <LegendColor
                            color={getColorPalette(orderPreSelectionData)[i]}
                          />
                          {data.label}: {data.value}
                        </LegendItem>
                      ))}
                    </LegendContainer>
                  </>
                )}

                {schedulingData && schedulingData.length > 0 && (
                  <>
                    <SectionTitle>Scheduling</SectionTitle>
                    <Text
                      fontSize="13px"
                      color="neutral.gray_600"
                      marginBottom="12px"
                    >
                      <p>
                        Out of all the different valid combinations (within
                        laycan and capacity constraints), by which reasons did
                        we drop some of them?
                      </p>
                      <p>
                        When there is a lot of flexibility in the schedule, the
                        number for better schedules is always high. If parameter
                        violations are high, you might get more efficient
                        results if you are able to tolerate e.g. higher delays.
                      </p>
                    </Text>

                    <ChartContainer>
                      {schedulingData.map((data, i) => (
                        <ChartSegment
                          width={data.pct}
                          color={getColorPalette(schedulingData)[i]}
                          key={i}
                        >
                          {Math.ceil(data.pct)}%
                        </ChartSegment>
                      ))}
                    </ChartContainer>

                    <LegendContainer>
                      {schedulingData.map((data, i) => (
                        <LegendItem
                          key={i}
                          title={LOG_REASON_TO_HINT[data.reason]}
                        >
                          <LegendColor
                            color={getColorPalette(schedulingData)[i]}
                          />
                          {data.label}: {data.value}
                        </LegendItem>
                      ))}
                    </LegendContainer>
                  </>
                )}

                {violationsData && violationsData.length > 0 && (
                  <>
                    <SectionTitle>Parameter violations</SectionTitle>
                    <Text
                      fontSize="13px"
                      color="neutral.gray_600"
                      marginBottom="12px"
                    >
                      <p>
                        Which parameters caused combinations to be discarded?
                      </p>{' '}
                      <p>
                        The same combination may count towards multiple
                        parameters at once.
                      </p>
                    </Text>

                    <ChartContainer>
                      {violationsData.map((data, i) => (
                        <ChartSegment
                          width={data.pct}
                          color={getColorPalette(violationsData)[i]}
                          key={i}
                        >
                          {Math.ceil(data.pct)}%
                        </ChartSegment>
                      ))}
                    </ChartContainer>

                    <LegendContainer>
                      {violationsData.map((data, i) => (
                        <LegendItem
                          key={i}
                          title={
                            METRICS_VIOLATION_TO_HINT[
                              data.violation as keyof ScheduleMetrics['violations']
                            ]
                          }
                        >
                          <LegendColor
                            color={getColorPalette(violationsData)[i]}
                          />
                          {data.label}: {data.value}
                        </LegendItem>
                      ))}
                    </LegendContainer>
                  </>
                )}
              </PanelBody>
            </Panel>
          </SelectionIndicators>
        </ReportContainer>
      </ModalBody>
    </Modal>
  );
}

export default VoyageSuggestionReport;
